import type { ChildrenCategoryData } from './schema';

export const PROJECTION_YEARS = 20;
export const MONTHS_IN_YEAR = 12;
export const DECIMAL_PRECISION = 2;

export const categories = [
  {
    id: 'middleHighSchoolers',
    icon: 'bookOpen',
    activeIconClass: 'bg-brand-orange/30',
    color: 'bg-brand-orange',
    supplies: [
      {
        label: 'Fournitures scolaires',
        budget: 70,
        cashback: {
          percent: 0.0352,
        },
        stores: ['Intermarché', 'Monoprix', 'Carrefour'],
      },
      {
        label: 'Tenue vestimentaire complète',
        budget: 130,
        cashback: {
          percent: 0.0629,
        },
        stores: ['C&A', 'Du Pareil au Même', 'H&M'],
      },
      {
        label: 'Photo souvenir encadrée',
        budget: 33,
        cashback: { percent: 0.192 },
        stores: ['Cheerz'],
      },
    ],
  },
  {
    id: 'schoolChildren',
    icon: 'graduationCap',
    activeIconClass: 'bg-brand-blue/30',
    color: 'bg-brand-blue',
    supplies: [
      {
        label: 'Fournitures scolaires + sac à dos',
        budget: 433,
        cashback: {
          percent: 0.0352,
        },
        stores: ['Intermarché', 'Monoprix', 'Carrefour'],
      },
      {
        label: 'Paire de sneakers stylées',
        budget: 100,
        cashback: {
          percent: 0.0704,
        },
        stores: ['Spartoo', 'Nike', 'adidas'],
      },
      {
        label: 'Ouvrages scolaires',
        budget: 60,
        cashback: { percent: 0.0576 },
        stores: ['Furet du Nord', 'Maxilivres', 'Fnac'],
      },
    ],
  },
  {
    id: 'universityStudents',
    icon: 'users',
    activeIconClass: 'bg-brand-purple/30',
    color: 'bg-brand-purple',
    supplies: [
      {
        label: 'Matériel informatique',
        budget: 1_200,
        cashback: {
          percent: 0.0258,
        },
        stores: ['Fnac', 'Darty', 'Apple'],
      },
      {
        label: 'Aménagement logement',
        budget: 300,
        cashback: {
          percent: 0.0448,
        },
        stores: ['IKEA', 'But', 'Maisons du monde'],
      },
      {
        label: 'Garde-robe au top',
        budget: 300,
        cashback: {
          percent: 0.0629,
        },
        stores: ['Zalando', 'adidas', 'H&M'],
      },
    ],
  },
] as const satisfies ChildrenCategoryData[];

type Store = {
  cashback: {
    percent: number;
  };
  name: string;
};

export type BudgetCategory = {
  averageCashback: {
    percent: number;
  };
  id: 'food' | 'housing' | 'shopping' | 'subscriptions';
  stores: Store[];
};

export type BudgetCategoryId = BudgetCategory['id'];

export const MAX_BUDGET = 10_000;

export const familyBudgets = {
  0: { food: 450, subscriptions: 50, housing: 90, shopping: 80 }, // Couple
  1: { food: 650, subscriptions: 65, housing: 125, shopping: 105 }, // Couple + 1 child
  2: { food: 850, subscriptions: 70, housing: 150, shopping: 150 }, // Couple + 2 children
  3: { food: 1000, subscriptions: 85, housing: 200, shopping: 180 }, // Couple + 3 children
  4: { food: 1100, subscriptions: 90, housing: 250, shopping: 200 }, // Couple + 4+ children
} satisfies Record<number, Record<BudgetCategoryId, number>>;

export const budgetCategories = [
  {
    id: 'food',
    averageCashback: { percent: 0.0377 },
    stores: [
      {
        name: 'Intermarché',
        cashback: { percent: 0.032 },
      },
      {
        name: 'Monoprix',
        cashback: { percent: 0.0352 },
      },
      { name: 'Carrefour', cashback: { percent: 0.0384 } },
    ],
  },
  {
    id: 'subscriptions',
    averageCashback: { percent: 0.0512 },
    stores: [
      { name: 'Netflix', cashback: { percent: 0.032 } },
      { name: 'Uber Eats', cashback: { percent: 0.0256 } },
      { name: 'Deezer', cashback: { percent: 0.1152 } },
    ],
  },
  {
    id: 'shopping',
    averageCashback: { percent: 0.0546 },
    stores: [
      { name: 'Zalando', cashback: { percent: 0.064 } },
      { name: 'H&M', cashback: { percent: 0.048 } },
      { name: 'adidas', cashback: { percent: 0.0768 } },
    ],
  },
  {
    id: 'housing',
    averageCashback: { percent: 0.041 },
    stores: [
      { name: 'Maison du monde', cashback: { percent: 0.0576 } },
      { name: 'IKEA', cashback: { percent: 0.032 } },
      { name: 'But', cashback: { percent: 0.0448 } },
    ],
  },
] as const satisfies BudgetCategory[];
